/**
 * RecipeBuilder — Tier-1 Pro tool.
 *
 * Draft a small-batch freeze-dryer recipe (ingredient list with quantities),
 * then run Claude Sonnet 4.6 analysis for:
 *   - macro breakdown (water / fat / salt / sugar / fiber / protein)
 *   - predicted freeze + primary-dry + secondary-dry cycle time with rationale
 *   - denaturing risk per heat-sensitive ingredient
 *   - recommended shelf temp / vacuum / tray depth
 *   - stability + rehydration guidance
 *
 * Free users see a frosted preview + upgrade CTA via <ProTierGate>.
 * Endpoint contracts live in /app/backend/recipe_builder_routes.py.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Plus, Trash2, ChefHat, Sparkles, Loader2, Save, Beaker,
  Clock, ThermometerSun, AlertTriangle, Package, Droplet, FileText,
  BarChart3, CalendarDays, Recycle,
} from 'lucide-react';
import ProTierGate from '../components/tools/ProTierGate';
import PageHero from '../components/PageHero';

const UNITS = ['g', 'kg', 'oz', 'lb', 'ml', 'l', 'cup', 'tbsp', 'tsp'];

const RiskBadge = ({ risk }) => {
  const map = {
    high:   'bg-rose-100 text-rose-800 border-rose-300',
    medium: 'bg-amber-100 text-amber-800 border-amber-300',
    low:    'bg-emerald-100 text-emerald-800 border-emerald-300',
  };
  const label = (risk || 'low').toString().toLowerCase();
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${map[label] || map.low}`}
      data-testid={`risk-badge-${label}`}
    >
      {label}
    </span>
  );
};

const MacroBar = ({ label, grams, total, color }) => {
  const pct = total ? Math.min(100, Math.round((grams / total) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground">{label}</span>
        <span className="text-muted-foreground">
          {grams.toFixed(1)}g · {pct}%
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AnalysisPanel = ({ analysis }) => {
  if (!analysis) return null;
  const m = analysis.macros || {};
  const total = m.total_weight_g || 1;
  return (
    <div className="space-y-4" data-testid="recipe-analysis-panel">
      {/* Macros */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Beaker className="w-4 h-4 text-primary" /> Macro breakdown ({total.toFixed(0)}g total)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <MacroBar label="Water"   grams={m.water_g   || 0} total={total} color="bg-sky-500"    />
          <MacroBar label="Fat"     grams={m.fat_g     || 0} total={total} color="bg-amber-500"  />
          <MacroBar label="Salt"    grams={m.salt_g    || 0} total={total} color="bg-slate-500"  />
          <MacroBar label="Sugar"   grams={m.sugar_g   || 0} total={total} color="bg-rose-500"   />
          <MacroBar label="Fiber"   grams={m.fiber_g   || 0} total={total} color="bg-emerald-500"/>
          <MacroBar label="Protein" grams={m.protein_g || 0} total={total} color="bg-violet-500" />
        </CardContent>
      </Card>

      {/* Cycle prediction */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Predicted cycle time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Freeze',        analysis.predicted_freeze_time_hours,        'sky'],
              ['Primary dry',   analysis.predicted_primary_dry_hours,        'amber'],
              ['Secondary dry', analysis.predicted_secondary_dry_hours,      'emerald'],
              ['Total cycle',   analysis.predicted_total_cycle_hours,        'primary'],
            ].map(([label, hrs, tone]) => (
              <div key={label} className={`rounded-xl p-3 bg-${tone}-50 border border-${tone}-200`} data-testid={`cycle-${label.toLowerCase().replace(/ /g,'-')}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                <div className="mt-1 text-xl font-extrabold text-foreground">
                  {Number(hrs || 0).toFixed(1)}<span className="text-sm font-normal text-muted-foreground"> h</span>
                </div>
              </div>
            ))}
          </div>
          {analysis.time_rationale && (
            <p className="mt-3 text-sm text-muted-foreground italic" data-testid="cycle-rationale">
              {analysis.time_rationale}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Denaturing risk */}
      {analysis.denaturing?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Denaturing & heat sensitivity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {analysis.denaturing.map((item, i) => (
              <div key={i} className="rounded-lg border border-border p-3" data-testid={`denaturing-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-foreground">{item.name}</div>
                  <RiskBadge risk={item.denaturing_risk} />
                </div>
                {item.heat_sensitive_compounds?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.heat_sensitive_compounds.map((c, j) => (
                      <span key={j} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{c}</span>
                    ))}
                  </div>
                )}
                {item.notes && <p className="mt-1 text-xs text-muted-foreground">{item.notes}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Cycle parameters */}
      {analysis.recommended_cycle && Object.keys(analysis.recommended_cycle).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ThermometerSun className="w-4 h-4 text-primary" /> Recommended cycle parameters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(analysis.recommended_cycle).map(([k, v]) => (
                <div key={k} className="rounded-xl p-3 bg-muted border border-border" data-testid={`cycle-param-${k}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{k.replace(/_/g, ' ')}</div>
                  <div className="mt-1 text-lg font-bold">{String(v)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stability + rehydration */}
      {(analysis.stability_notes || analysis.rehydration_notes) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" /> Storage & rehydration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {analysis.stability_notes && (
              <div className="text-sm text-foreground" data-testid="stability-notes">
                <span className="font-bold">Storage: </span>{analysis.stability_notes}
              </div>
            )}
            {analysis.rehydration_notes && (
              <div className="text-sm text-foreground" data-testid="rehydration-notes">
                <span className="font-bold flex items-center gap-1"><Droplet className="w-3 h-3" /> Rehydration: </span>{analysis.rehydration_notes}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Extended cycle-profile knobs for mid- to high-end freeze dryers */}
      {analysis.cycle_profile && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ThermometerSun className="w-4 h-4 text-primary" /> Cycle profile (advanced)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="cycle-profile">
              {[
                ['Vacuum shelf temp',        analysis.cycle_profile.vacuum_shelf_temp_f,           '°F'],
                ['Vacuum transition time',   analysis.cycle_profile.vacuum_transition_time_min,    'min'],
                ['Primary dry transition',   analysis.cycle_profile.primary_dry_transition_temp_f, '°F'],
                ['Primary dry ramp rate',    analysis.cycle_profile.primary_dry_ramp_rate_deg_per_sec, '°F/s'],
                ['System max temp',          analysis.cycle_profile.system_max_temp_f,             '°F'],
                ['Secondary dry ramp rate',  analysis.cycle_profile.secondary_dry_ramp_rate_deg_per_sec, '°F/s'],
                ['Total completion time',    analysis.cycle_profile.total_completion_time_hours,   'h'],
              ].map(([label, val, unit]) => (
                <div key={label} className="rounded-lg p-2 border border-border bg-muted/30" data-testid={`cp-${String(label).toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                  <div className="mt-0.5 text-sm font-bold text-foreground">
                    {Number.isFinite(val) && val !== 0 ? `${Number(val).toFixed(unit === '°F/s' ? 4 : 1)} ${unit}` : '—'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wet vs dry weight tiles */}
      {(analysis.estimated_wet_weight_g > 0 || analysis.estimated_dry_weight_g > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl p-3 border border-sky-200 bg-sky-50" data-testid="wet-weight-tile">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Anticipated wet weight</div>
            <div className="mt-1 text-2xl font-extrabold text-foreground">{analysis.estimated_wet_weight_g.toFixed(0)}<span className="text-sm font-normal text-muted-foreground"> g</span></div>
          </div>
          <div className="rounded-xl p-3 border border-emerald-200 bg-emerald-50" data-testid="dry-weight-tile">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Anticipated dry weight</div>
            <div className="mt-1 text-2xl font-extrabold text-foreground">{analysis.estimated_dry_weight_g.toFixed(0)}<span className="text-sm font-normal text-muted-foreground"> g</span></div>
          </div>
        </div>
      )}

      {/* Nutrition Facts panel — FDA-style */}
      {analysis.nutrition_label && (
        <Card data-testid="nutrition-label">
          <CardContent className="p-4">
            <div className="mx-auto max-w-sm border-2 border-black rounded-md p-3 bg-white text-black font-sans">
              <div className="text-2xl font-black leading-none border-b-8 border-black pb-1">Nutrition Facts</div>
              <div className="mt-1 text-xs">
                {(analysis.nutrition_label.servings_per_batch || 0).toFixed(1)} servings per batch
              </div>
              <div className="flex justify-between text-sm font-bold border-b-4 border-black py-1">
                <span>Serving size</span>
                <span>{(analysis.nutrition_label.serving_size_g || 0).toFixed(0)} g</span>
              </div>
              <div className="pt-1 text-[10px] font-bold uppercase">Amount per serving</div>
              <div className="flex justify-between items-end border-b-4 border-black pb-1">
                <span className="text-2xl font-black">Calories</span>
                <span className="text-3xl font-black">{(analysis.nutrition_label.calories_kcal || 0).toFixed(0)}</span>
              </div>
              {[
                ['Total Fat',        'total_fat_g',        'g',  true,  null],
                ['Saturated Fat',    'saturated_fat_g',    'g',  false, 'ml-2'],
                ['Trans Fat',        'trans_fat_g',        'g',  false, 'ml-2'],
                ['Cholesterol',      'cholesterol_mg',     'mg', true,  null],
                ['Sodium',           'sodium_mg',          'mg', true,  null],
                ['Total Carbohydrate','total_carb_g',      'g',  true,  null],
                ['Dietary Fiber',    'dietary_fiber_g',    'g',  false, 'ml-2'],
                ['Total Sugars',     'total_sugars_g',     'g',  false, 'ml-2'],
                ['Added Sugars',     'added_sugars_g',     'g',  false, 'ml-4'],
                ['Protein',          'protein_g',          'g',  true,  null],
              ].map(([label, key, unit, bold, indent]) => (
                <div key={key} className={`flex justify-between text-sm border-b border-black py-0.5 ${indent || ''}`}>
                  <span className={bold ? 'font-bold' : ''}>{label}</span>
                  <span>{(analysis.nutrition_label[key] || 0).toFixed(1)} {unit}</span>
                </div>
              ))}
              <div className="pt-1 border-t-4 border-black text-xs space-y-0.5">
                <div className="flex justify-between"><span>Vitamin D</span><span>{(analysis.nutrition_label.vitamin_d_mcg || 0).toFixed(1)} mcg</span></div>
                <div className="flex justify-between"><span>Calcium</span><span>{(analysis.nutrition_label.calcium_mg || 0).toFixed(0)} mg</span></div>
                <div className="flex justify-between"><span>Iron</span><span>{(analysis.nutrition_label.iron_mg || 0).toFixed(1)} mg</span></div>
                <div className="flex justify-between"><span>Potassium</span><span>{(analysis.nutrition_label.potassium_mg || 0).toFixed(0)} mg</span></div>
              </div>
              <div className="mt-2 text-[9px] italic text-slate-600">
                Estimates from AI analysis — informational only, not an FDA-audited label.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {analysis.warnings?.length > 0 && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-3" data-testid="analysis-warnings">
          <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 mb-1">Warnings</div>
          <ul className="list-disc list-inside text-sm text-rose-800 space-y-0.5">
            {analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inner tool — assumes Pro (gated by ProTierGate).
// ---------------------------------------------------------------------------
const RecipeBuilderInner = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [machineType, setMachineType] = useState('');
  const [notes, setNotes] = useState('');
  const [unitSystem, setUnitSystem] = useState('imperial');
  const [startingState, setStartingState] = useState('ambient');
  const [ingredients, setIngredients] = useState([
    { name: '', quantity: '', unit: 'g' },
  ]);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [saved, setSaved] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importList, setImportList] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  const openImport = async () => {
    setImportOpen(true);
  };
  useEffect(() => {
    if (!importOpen) return;
    setImportLoading(true);
    axios.get(`${API}/tools/recipe-center`, { params: { limit: 100 } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.items || data?.recipes || []);
        setImportList(list);
      })
      .catch(() => setImportList([]))
      .finally(() => setImportLoading(false));
  }, [importOpen, API]);

  const _unitOf = (u) => {
    const s = String(u || '').toLowerCase().trim();
    return UNITS.includes(s) ? s : 'g';
  };
  const importCookbookRecipe = (r) => {
    setName(r.name || r.title || 'Imported recipe');
    setMachineType(r.machine || r.machine_type || '');
    setNotes(r.notes || r.description || '');
    const rows = (r.ingredients || []).map((ing) => ({
      name: ing.name || ing.item || '',
      quantity: String(ing.quantity ?? ing.amount ?? ''),
      unit: _unitOf(ing.unit),
    })).filter(x => x.name);
    setIngredients(rows.length ? rows : [{ name: '', quantity: '', unit: 'g' }]);
    setAnalysis(null);
    setSelectedId(null);
    setImportOpen(false);
    toast.success(`Imported "${r.name || r.title}". Click Analyze to score it.`);
  };

  const loadSaved = async () => {
    try {
      const { data } = await axios.get(`${API}/tools/recipe-builder`);
      setSaved(Array.isArray(data) ? data : []);
    } catch (err) {
      // 402 or other — swallow (Pro gate handles the messaging)
    }
  };
  useEffect(() => { loadSaved(); }, []);

  const addRow = () => setIngredients([...ingredients, { name: '', quantity: '', unit: 'g' }]);
  const removeRow = (i) => setIngredients(ingredients.filter((_, idx) => idx !== i));
  const updateRow = (i, key, value) => {
    const next = [...ingredients];
    next[i] = { ...next[i], [key]: value };
    setIngredients(next);
  };

  const payload = useMemo(() => ({
    name: name.trim(),
    machine_type: machineType.trim() || null,
    notes: notes.trim() || null,
    unit_system: unitSystem,
    starting_state: startingState,
    ingredients: ingredients
      .filter(r => r.name.trim() && Number(r.quantity) > 0)
      .map(r => ({ name: r.name.trim(), quantity: Number(r.quantity), unit: r.unit })),
  }), [name, machineType, notes, unitSystem, startingState, ingredients]);

  const canAnalyze = payload.name.length > 0 && payload.ingredients.length > 0 && !analyzing;

  const runAnalysis = async () => {
    if (!canAnalyze) {
      toast.error('Add a recipe name and at least one ingredient with a quantity.');
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const { data } = await axios.post(`${API}/tools/recipe-builder/analyze`, payload);
      setAnalysis(data);
      toast.success('Recipe analyzed with Claude Sonnet');
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Analysis failed';
      toast.error(typeof msg === 'string' ? msg : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveRecipe = async () => {
    if (!canAnalyze) {
      toast.error('Add a recipe name and at least one ingredient with a quantity.');
      return;
    }
    setSaving(true);
    try {
      // If we already have an analysis in memory, pass it back so the
      // backend doesn't pay Claude twice for the analyze-then-save flow.
      const body = analysis ? { ...payload, analysis } : payload;
      const { data } = await axios.post(`${API}/tools/recipe-builder`, body);
      setAnalysis(data.analysis);
      setSelectedId(data.id);
      toast.success('Recipe saved to your Pro library');
      loadSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadRecipe = async (recipeId) => {
    try {
      const { data } = await axios.get(`${API}/tools/recipe-builder/${recipeId}`);
      setName(data.name);
      setMachineType(data.machine_type || '');
      setNotes(data.notes || '');
      setIngredients(data.ingredients?.length
        ? data.ingredients.map(i => ({ name: i.name, quantity: String(i.quantity), unit: i.unit }))
        : [{ name: '', quantity: '', unit: 'g' }]);
      setAnalysis(data.analysis);
      setSelectedId(data.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error('Failed to load recipe');
    }
  };

  const deleteRecipe = async (recipeId) => {
    if (!window.confirm('Delete this recipe? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/tools/recipe-builder/${recipeId}`);
      toast.success('Recipe deleted');
      if (selectedId === recipeId) {
        setSelectedId(null);
        setAnalysis(null);
      }
      loadSaved();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="recipe-builder-inner">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="rb-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <div className="text-[10px] uppercase tracking-widest font-extrabold text-primary">
          Tier-1 · Pro Tool
        </div>
      </div>

      <PageHero
        icon={<ChefHat className="w-8 h-8" />}
        eyebrow="Recipe Builder"
        title="Design a batch, then let AI predict the cycle"
        subtitle="Type your ingredients + quantities and Claude Sonnet 4.6 models the macros, freeze-dry time, denaturing risk, and cycle parameters. Save recipes to feed Batch Analyzer + Production Forecast."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: builder form */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recipe details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recipe name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Beef stroganoff MRE"
                  className="mt-1"
                  data-testid="rb-name-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Machine (optional)</label>
                  <Input
                    value={machineType}
                    onChange={(e) => setMachineType(e.target.value)}
                    placeholder="e.g. Harvest Right Medium"
                    className="mt-1"
                    data-testid="rb-machine-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Notes (optional)</label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. pre-cooked, ambient prep"
                    className="mt-1"
                    data-testid="rb-notes-input"
                  />
                </div>
              </div>
              {/* Unit system + starting product state controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Unit system</label>
                  <div className="mt-1 flex gap-2">
                    {[['imperial', '°F · g'], ['metric', '°C · g']].map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setUnitSystem(k)}
                        className={`flex-1 px-3 py-2 rounded-md text-xs font-bold border ${unitSystem === k ? 'bg-primary text-white border-primary' : 'bg-background border-border text-foreground'}`}
                        data-testid={`rb-units-${k}`}
                      >{label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Starting product state</label>
                  <select
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
                    value={startingState}
                    onChange={(e) => setStartingState(e.target.value)}
                    data-testid="rb-starting-state"
                  >
                    <option value="ambient">Ambient / room temperature</option>
                    <option value="refrigerated">Refrigerated (~4°C)</option>
                    <option value="frozen">Home-frozen (~-18°C)</option>
                    <option value="pre_frozen">Commercially pre-frozen (below -30°C)</option>
                  </select>
                  {(startingState === 'frozen' || startingState === 'pre_frozen') && (
                    <div className="mt-1 text-[10px] text-amber-700 italic" data-testid="rb-frozen-note">
                      AI will assess whether the freeze stage can be safely skipped for your machine.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Ingredients</CardTitle>
              <Button size="sm" variant="outline" onClick={addRow} className="rounded-full font-bold" data-testid="rb-add-ingredient-btn">
                <Plus className="w-4 h-4 mr-1" /> Add row
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {ingredients.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`rb-ingredient-row-${i}`}>
                  <Input
                    className="col-span-6"
                    placeholder="Ingredient (e.g. cooked chicken breast)"
                    value={row.name}
                    onChange={(e) => updateRow(i, 'name', e.target.value)}
                    data-testid={`rb-ingredient-name-${i}`}
                  />
                  <Input
                    className="col-span-3"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Qty"
                    value={row.quantity}
                    onChange={(e) => updateRow(i, 'quantity', e.target.value)}
                    data-testid={`rb-ingredient-qty-${i}`}
                  />
                  <select
                    className="col-span-2 h-10 rounded-md border border-input bg-background px-2 text-sm"
                    value={row.unit}
                    onChange={(e) => updateRow(i, 'unit', e.target.value)}
                    data-testid={`rb-ingredient-unit-${i}`}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="col-span-1 text-muted-foreground hover:text-rose-600"
                    onClick={() => removeRow(i)}
                    disabled={ingredients.length === 1}
                    data-testid={`rb-remove-ingredient-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={runAnalysis}
              disabled={!canAnalyze}
              className="rounded-full font-extrabold bg-primary text-white hover:bg-primary/90"
              data-testid="rb-analyze-btn"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {analyzing ? 'Analyzing…' : 'Analyze with AI'}
            </Button>
            <Button
              onClick={saveRecipe}
              disabled={!canAnalyze || saving}
              variant="outline"
              className="rounded-full font-bold"
              data-testid="rb-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save recipe
            </Button>
            <Button
              onClick={() => setImportOpen(true)}
              variant="ghost"
              className="rounded-full font-bold"
              data-testid="rb-import-btn"
            >
              <FileText className="w-4 h-4 mr-1" /> Import from Cookbook
            </Button>
          </div>

          {importOpen && (
            <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50" onClick={() => setImportOpen(false)} data-testid="rb-import-dialog">
              <div className="bg-background rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading text-lg font-extrabold">Import from Community Cookbook</div>
                  <Button variant="ghost" size="sm" onClick={() => setImportOpen(false)} data-testid="rb-import-close">✕</Button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Pick a shared recipe to seed the form. AI analysis is not copied — click Analyze to re-run it against your unit + machine settings.
                </p>
                <div className="flex-1 overflow-y-auto space-y-1" data-testid="rb-import-list">
                  {importLoading && <div className="text-sm text-muted-foreground p-2">Loading…</div>}
                  {!importLoading && importList.length === 0 && (
                    <div className="text-sm text-muted-foreground p-2 italic">No shared recipes in the Cookbook yet.</div>
                  )}
                  {importList.map((r) => (
                    <button
                      key={r.id}
                      className="w-full text-left p-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5"
                      onClick={() => importCookbookRecipe(r)}
                      data-testid={`rb-import-row-${r.id}`}
                    >
                      <div className="font-bold text-foreground">{r.name || r.title}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {(r.ingredients?.length || r.ingredient_count || 0)} ingredients
                        {r.machine ? ` · ${r.machine}` : ''}
                        {r.author_name ? ` · by ${r.author_name}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <AnalysisPanel analysis={analysis} />

          {/* Cross-tool deep links — only surface once the recipe is saved
              (has an id) so the destination tools can locate it via
              ?recipe_id / ?recipe_ids. */}
          {selectedId && (
            <Card data-testid="rb-cross-tool-links">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> Push this recipe to another Pro tool
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  className="rounded-full font-bold justify-start"
                  onClick={() => navigate(`/tools/batch-analyzer?recipe_ids=${selectedId}`)}
                  data-testid="rb-goto-batch-analyzer"
                >
                  <BarChart3 className="w-4 h-4 mr-1" /> Analyze cost
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full font-bold justify-start"
                  onClick={() => navigate(`/tools/production-forecast?recipe_id=${selectedId}`)}
                  data-testid="rb-goto-production-forecast"
                >
                  <CalendarDays className="w-4 h-4 mr-1" /> Schedule this recipe
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full font-bold justify-start"
                  onClick={() => navigate(`/tools/waste-cost-optimizer?recipe_id=${selectedId}`)}
                  data-testid="rb-goto-waste-cost-optimizer"
                >
                  <Recycle className="w-4 h-4 mr-1" /> Optimize this recipe
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: saved recipe library */}
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" /> My saved recipes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="rb-saved-list">
              {saved.length === 0 && (
                <div className="text-sm text-muted-foreground italic">
                  No saved recipes yet. Analyze + save one to build your library.
                </div>
              )}
              {saved.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-xl border p-3 hover:border-primary/50 transition-colors ${selectedId === r.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                  data-testid={`rb-saved-row-${r.id}`}
                >
                  <button
                    className="text-left w-full"
                    onClick={() => loadRecipe(r.id)}
                    data-testid={`rb-saved-open-${r.id}`}
                  >
                    <div className="font-bold text-foreground truncate">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.ingredients?.length || 0} ingredients · {Number(r.analysis?.predicted_total_cycle_hours || 0).toFixed(0)}h cycle
                    </div>
                  </button>
                  <div className="mt-2 flex items-center justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteRecipe(r.id)}
                      className="text-rose-600 hover:bg-rose-50 rounded-full"
                      data-testid={`rb-saved-delete-${r.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const RecipeBuilder = () => (
  <ProTierGate
    toolName="Recipe Builder"
    tagline="AI-modeled freeze-dry cycles, denaturing risk warnings, and macro-driven time predictions for every batch you design."
    data_testid_prefix="recipe-builder-gate"
  >
    <RecipeBuilderInner />
  </ProTierGate>
);

export default RecipeBuilder;
